import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FeedFavoriteButton } from './FeedFavoriteButton';
import { useFeedFavorites } from '@/hooks/useFeedFavorites';
import { useI18n } from '@/hooks/useI18n';
import { useInterval } from '@/hooks/useInterval';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { useShell } from '@/hooks/useShell';
import { routes } from '@/lib/routes';
import type { FeedBucket } from '@/types';
import type { TranslationKey } from '@/i18n';
import { digestItems } from '../data';
import type { DigestItem as ApiDigestItem } from '@/lib/api/types';
import { matchesFeedTag, normalizeFeedTag } from '@/lib/feedTagFilter';

const TAB_ICONS: Record<FeedBucket, ReactNode> = {
  fresh: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  important: <path d="M12 3 5 13h6l-1 8 9-12h-6z" />,
  pending: <path d="M4 17l5-5 4 3 7-8M16 7h4v4" />,
};

const tabs: { bucket: FeedBucket; apiBucket: string; labelKey: TranslationKey; label: string }[] = [
  { bucket: 'fresh', apiBucket: 'LATEST', labelKey: 'digest.tab.fresh', label: 'Последние' },
  { bucket: 'important', apiBucket: 'IMPORTANT', labelKey: 'digest.tab.important', label: 'Важные' },
  { bucket: 'pending', apiBucket: 'TRENDING', labelKey: 'digest.tab.pending', label: 'Тренды' },
];

function humanizeSource(source: string): string {
  const map: Record<string, string> = {
    Owner: 'Владелец',
    OWNER: 'Владелец',
    SYSTEM_OWNER: 'Владелец',
    Platform: 'Платформа',
  };
  return map[source] ?? source;
}

function formatMeeting(item: ApiDigestItem): string | null {
  if (!item.meeting_at) return null;
  const when = new Date(item.meeting_at);
  const date = when.toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  const bits = [date, item.meeting_format, item.meeting_place].filter(Boolean);
  return bits.join(' · ');
}

export const DigestPanel = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { shell } = useShell();
  const [params, setParams] = useSearchParams();
  const reducedMotion = usePrefersReducedMotion();
  const { isFavorite, toggleFavorite, canFavorite } = useFeedFavorites();
  const [paused, setPaused] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  const initialBucket =
    (tabs.find((tab) => tab.apiBucket === params.get('digest'))?.bucket as FeedBucket | undefined) ||
    'important';
  const [bucket, setBucket] = useState<FeedBucket>(initialBucket);

  const apiDigest = shell?.digest ?? [];
  const activeTab = tabs.find((tab) => tab.bucket === bucket) ?? tabs[0];
  const activeTag = normalizeFeedTag(params.get('tag') || params.get('q'));

  const items = useMemo(() => {
    if (apiDigest.length > 0) {
      return apiDigest
        .filter((item) => item.bucket === activeTab.apiBucket)
        .filter((item) =>
          matchesFeedTag(activeTag, [
            item.source,
            item.title,
            item.summary,
            item.regulation_title,
            item.regulation_type,
            item.content_type,
          ]),
        )
        .map((item) => ({
          raw: item,
          id: item.id,
          kind: humanizeSource(item.source),
          title: item.title,
          summary: item.summary || '',
          ago: '',
          colors: [item.accent, item.accent] as [string, string],
          href: item.event_id
            ? item.href || `${routes.owner}/meetings`
            : item.href || routes.portalSection('news'),
          deadline: item.countdown_state,
          deadlineAt: item.deadline_at,
          meeting: formatMeeting(item),
          regulation:
            item.regulation_title || item.regulation_type
              ? [item.regulation_type, item.regulation_title, item.regulation_version]
                  .filter(Boolean)
                  .join(' · ')
              : null,
          officialLabel: item.official_source_label,
          officialUrl: item.official_source_url,
          important: item.bucket === 'IMPORTANT',
        }));
    }
    return digestItems
      .filter((item) => item.bucket === bucket)
      .filter((item) =>
        matchesFeedTag(activeTag, [t(item.kindKey), t(item.titleKey), t(item.agoKey)]),
      )
      .map((item) => ({
        raw: null as ApiDigestItem | null,
        id: item.id,
        kind: humanizeSource(t(item.kindKey)),
        title: t(item.titleKey),
        summary: '',
        ago: t(item.agoKey),
        colors: item.colors,
        href: routes.portalSection('news'),
        deadline: null as string | null,
        deadlineAt: null as string | null,
        meeting: null as string | null,
        regulation: null as string | null,
        officialLabel: null as string | null,
        officialUrl: null as string | null,
        important: bucket === 'important',
      }));
  }, [apiDigest, activeTab.apiBucket, bucket, t, activeTag]);

  useEffect(() => {
    setOffset(0);
  }, [bucket, items.length, activeTag]);

  useInterval(
    () => {
      if (!trackRef.current || items.length < 2) return;
      const card = trackRef.current.children[0] as HTMLElement | undefined;
      const step = card?.offsetHeight || 96;
      const max = Math.max(0, trackRef.current.scrollHeight - (trackRef.current.parentElement?.clientHeight ?? 0));
      setOffset((prev) => {
        const next = prev + step;
        return next > max ? 0 : next;
      });
    },
    paused || reducedMotion || items.length < 2 ? null : 7500,
  );

  const selectTab = (next: FeedBucket, apiBucket: string) => {
    setBucket(next);
    const nextParams = new URLSearchParams(params);
    nextParams.set('digest', apiBucket);
    setParams(nextParams, { replace: true });
  };

  return (
    <aside className="panel right-scroll digest-panel">
      <div className="right-tabs" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.bucket}
            type="button"
            role="tab"
            aria-selected={bucket === tab.bucket}
            onClick={() => selectTab(tab.bucket, tab.apiBucket)}
          >
            <span className="digest-tab-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">{TAB_ICONS[tab.bucket]}</svg>
            </span>
            <span>{t(tab.labelKey) || tab.label}</span>
          </button>
        ))}
      </div>

      <div
        className="right-window"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setPaused(false);
          }
        }}
      >
        <div
          className="right-track"
          role="tabpanel"
          ref={trackRef}
          style={{ transform: `translateY(-${offset}px)` }}
        >
          {items.length === 0 && (
            <p className="right-empty">
              {activeTag ? `Нет записей по #${activeTag}` : t('common.notFound')}
            </p>
          )}
          {items.map((item) => (
            <div
              key={item.id}
              role="button"
              tabIndex={0}
              className="right-card"
              style={{ '--c1': item.colors[0], '--c2': item.colors[1] } as CSSProperties}
              onClick={() => navigate(item.href)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(item.href);
                }
              }}
            >
              <span className="right-thumb" aria-hidden="true" />
              <div>
                <small>
                  {item.kind}
                  <FeedFavoriteButton
                    itemId={item.id}
                    isFavorite={isFavorite(item.id)}
                    disabled={!canFavorite}
                    onToggle={toggleFavorite}
                  />
                </small>
                <h4>{item.title}</h4>
                {item.summary ? <p>{item.summary}</p> : null}
                {item.important && item.deadline ? (
                  <p className="feed-meta feed-meta--deadline">Срок: {item.deadline}</p>
                ) : null}
                {!item.important && item.deadline ? (
                  <p className="feed-meta">Срок: {item.deadline}</p>
                ) : null}
                {item.meeting ? <p className="feed-meta feed-meta--meeting">{item.meeting}</p> : null}
                {item.regulation ? (
                  <p className="feed-meta feed-meta--regulation">{item.regulation}</p>
                ) : null}
                {item.officialLabel && item.officialUrl ? (
                  <a
                    className="feed-meta feed-meta--source"
                    href={item.officialUrl}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      navigate(item.officialUrl!);
                    }}
                  >
                    {item.officialLabel} →
                  </a>
                ) : null}
                {item.ago ? <p>{item.ago}</p> : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
};
